import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={location.state?.from || "/dashboard"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex flex-col lg:flex-row">
      {/* Left visual */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at top right, rgba(201,168,76,0.18), transparent 55%), radial-gradient(ellipse at bottom left, rgba(201,168,76,0.10), transparent 60%), linear-gradient(180deg, #0D0D0D 0%, #141414 100%)",
        }}
      >
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative z-10 m-auto px-16 max-w-xl">
          <div className="overline mb-6">Private circle · Dubai</div>
          <h2 className="font-serif text-[#F0EDE8] text-5xl leading-[1.05] tracking-tight">
            A table for the
            <br />
            <span className="text-[#C9A84C] italic">few who close</span>
            <br />
            the right deals.
          </h2>
          <p className="mt-8 text-[#9A9590] leading-relaxed max-w-sm font-light">
            Real estate. Finance. Legal. Marketing. Tech. One quiet room — five sectors,
            one win-win model.
          </p>
          <div className="mt-16 flex items-center gap-3 text-xs uppercase tracking-[0.32em] text-[#9A9590]/80">
            <span className="w-8 h-px bg-[#C9A84C]/60" />
            <span>By invitation only</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-12 py-12 lg:py-16">
        <div className="w-full max-w-sm animate-fade-in">
          {/* Mobile-only header */}
          <div className="lg:hidden text-center mb-10">
            <Logo size="md" />
            <p className="mt-4 text-[11px] uppercase tracking-[0.32em] text-[#B8B3AC]">
              Private Circle · Dubai
            </p>
          </div>

          <div className="hidden lg:block mb-12">
            <Logo size="lg" />
            <div className="mt-5 text-xs uppercase tracking-[0.36em] text-[#B8B3AC] text-left">
              Where the right people close the right deals.
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-7" data-testid="login-form">
            <div>
              <label className="overline">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@synergy-table.com"
                autoComplete="email"
                required
                className="ts-input"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="overline">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="ts-input"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="text-sm text-red-400/90 border-l-2 border-red-500/60 pl-3 py-1" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full py-3 rounded-md flex items-center justify-center gap-2 mt-2 disabled:opacity-70"
              data-testid="login-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? "Signing in" : "Enter the table"}
            </button>
          </form>

          <div className="mt-10 text-[11px] uppercase tracking-[0.3em] text-[#9A9590]/70 text-center lg:text-left">
            Access is invite-only. Contact your Group Organizer.
          </div>
        </div>
      </div>
    </div>
  );
}
